import get from 'lodash/get';

import { REQUEST_QUEUE_ENDED_EVENT } from 'root/request_queue';
import { getRequestFunction } from 'root/requestFunction';

export const REQUEST_QUEUE_AJAX_MESSAGES_EVENT = 'requestQueueAjaxMessages';

// See: https://chromium.googlesource.com/chromium/src/+/master/docs/security/url_display_guidelines/url_display_guidelines.md.
const URL_MAX_LENGTH = 2083;
const GET_PARAMS_MAX_LENGTH = URL_MAX_LENGTH - 283;

class ServerCall implements MerchantosServerCall {
  use_iframe: boolean;

  _requestQueueAjaxMessages: AjaxMessages[] = [];

  constructor() {
    this.use_iframe = false;
    this.bindRequestQueueEvents();
  }

  /**
   * Allow other parts of the application to access all the messages received from the server for a given request queue.
   * Often, the UI is injected in a separate subsequent server call that doesn't contain the forms ajax messages anymore.
   */
  bindRequestQueueEvents = () => {
    window.addEventListener(REQUEST_QUEUE_ENDED_EVENT, () => {
      dispatchEvent(
        new CustomEvent(REQUEST_QUEUE_AJAX_MESSAGES_EVENT, {
          detail: this._requestQueueAjaxMessages,
        })
      );
      // clearing the messages before next request queue
      this.flushAjaxMessages();
    });
  };

  bind = (
    data: ServerCallData,
    callback?: DoServerCallCallback,
    noError?: boolean,
    doAsPost?: boolean,
    debugObj?: { isRetry: boolean; statusCode: number }
  ) => {
    const search_params = new URLSearchParams();

    search_params.set('ajax', '1');
    search_params.set('no_cache', new Date().getTime().toString());

    // prevent repeat actions without a server response
    const prevent_repeat_id = window.merchantos.controls.prevent_repeat.attachToServerCall();

    if (data.form_name !== 'timeout_check') {
      window.merchantos.notifier.startWarningTimeout();
      window.merchantos.notifier.startAlertTimeout();
    }

    const type = window.getRequestType(data);

    // Makes sure the relevant info are traced in monitoring tools.
    // GET and POST data will be merged during the application startup,
    // so it will not change the final payload (see: `includes/startup.php`).

    const formName = get(data, 'form_name');
    const name = get(data, 'name', get(data, 'ajax_listing.name'));

    if (formName) {
      search_params.set('form_name', formName);
    }

    if (name) {
      search_params.set('name', name);
    }

    window.showNotifier('working');

    // @ts-expect-error ts-migrate(7022) FIXME: 'promise' implicitly has type 'any' because it doe... Remove this comment to see the full error message
    const promise = $.ajax({
      url: `/ajax_forms.php?${search_params.toString()}`,
      mimeType: 'text/json',
      type,
      data,
    })
      // eslint-disable-next-line consistent-return
      .done((data, _textStatus, _jqXHR) => {
        inFlightPanelXhrRequest = null;

        // free up the callee button/action
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | boolean' is not assigna... Remove this comment to see the full error message
        window.merchantos.controls.prevent_repeat.freeIt(prevent_repeat_id);

        window.resetIdleTimer(data);
        // make sure data is there
        if (!data) {
          window.alertUser('Could not complete request. If this continues contact support.');
          return promise;
        }
        if (window.merchantos.server_call.result(data)) {
          window.merchantos.interrupt.empty(data.form_name);
          return promise;
        }
        // Make sure we were passed a callback_fnc to call.
        if (typeof callback === 'function') {
          callback(data);
        }

        // Run the ajax functions after the DOM has been parsed
        this.evalCode(data.evalcode);

        window.merchantos.datepicker.init();
        window.merchantos.interrupt.process(data.form_name);
        window.numberScrollInputFix();
        window.autosizeLoader();

        // @NOTE: Track how many retry succeeds
        if (debugObj) {
          window.dataLayer.push({
            event: 'ServerCallRetry',
            isSuccessRetry: 'true',
            statusCode: debugObj.statusCode,
          });
        }
      })
      // eslint-disable-next-line consistent-return
      .fail((jqXHR, textStatus, errorThrown) => {
        window.merchantos.controls.prevent_repeat.freeAll();

        // Prevent error logging from throwing error (loop insanity)
        if (noError) {
          return promise;
        }

        // Prevent aborted requests to be logged or retried
        // e.g. When fast clicking multiple tabs, the previous tab request will be aborted
        // if the client didn't receive the response before the next tab is clicked
        if (textStatus === 'abort') {
          return;
        }

        // @NOTE: Track how many retry fails
        if (debugObj) {
          window.dataLayer.push({
            event: 'ServerCallRetry',
            isSuccessRetry: 'false',
            statusCode: debugObj.statusCode,
          });
        }

        // If we get a failed call that has no error, retry once immediately
        if (
          !errorThrown &&
          !debugObj &&
          ![400, 401, 403, 404, 405, 429, 500, 503].includes(jqXHR?.status) &&
          window.lsCloud.Modules.hasModule('gui.server_call_retry')
        ) {
          if (window.lsCloud.Modules.hasModule('gui.server_call_debug')) {
            console.debug('-------- server_call failed debug for DroppedRequests --------');
            console.debug('jqXHR', jqXHR, JSON.stringify(jqXHR));
            console.debug('textStatus', textStatus);
            console.debug('errorThrown', errorThrown, JSON.stringify(errorThrown));
            console.debug('-------- end debug --------');
          }

          // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'ContentMap' is not assignable to... Remove this comment to see the full error message
          window.merchantos.server_call.log_dropped_request(data, type, textStatus, jqXHR);
          if (data.form_name === 'register.add_item_search') {
            window.location.reload();
          } else if (type === 'GET') {
            window.merchantos.server_call.bind(data, callback, noError, doAsPost, {
              isRetry: true,
              statusCode: jqXHR?.status,
            });
          }
          return promise;
        }
        if (errorThrown && typeof errorThrown === 'string') {
          if (errorThrown.match(/Invalid JSON/)) {
            if (jqXHR.responseText) {
              // eslint-disable-next-line no-param-reassign
              errorThrown = jqXHR.responseText;
            }
          }
        }
        if (data) {
          if (data.pannel_id) {
            // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'JQuery<HTMLElement>' is not assi... Remove this comment to see the full error message
            window.clearWorking($(`#${data.pannel_id}`));
          }
          if (data.form_name) {
            window.merchantos.interrupt.process(data.form_name);
          }
        }

        if (jqXHR.statusText !== 'abort') {
          const alertMessage =
            jqXHR?.responseJSON?.failed_msg ||
            'Unable to contact server. You may be having a problem with your internet connection. If this continues contact support';

          if (typeof jqXHR.responseText !== 'undefined') {
            let jsonData;
            try {
              jsonData = JSON.parse(jqXHR.responseText);
            } finally {
              if (jsonData && jsonData.error && typeof jsonData.error === 'string') {
                window.alertUser(jsonData.error);
              } else {
                window.alertUser(alertMessage);
              }
            }
          } else {
            window.alertUser(alertMessage);
          }
        }
      })
      .always(() => window.hideNotifier('working'));
    window.merchantos.asyncSubscribe.notifySubscribers(data.form_name, data, promise);
    return promise;
  };

  result = (data: AjaxFormsResponse) => {
    let delay;
    if (!window || window.closing) return true;

    if (data.goto_url) {
      window.merchantos.utils.openUrl(data.goto_url);
      return true;
    }

    if (data.post_to_url && data.post_vars) {
      // write a form at the bottom of the body and put the post vars in there and then submit() the form
      const form = document.createElement('form');
      form.method = 'post';
      form.action = data.post_to_url;

      // eslint-disable-next-line
      for (const post_field in data.post_vars) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = post_field;
        input.value = data.post_vars[post_field];
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
      return true;
    }

    // TODO: safe to rely only on data.logout_url; fallback on data.logout is
    // only included for release/rollback compatibility
    if (data.logout || data.logout_url) {
      let logout_url = '/register.php';
      if (data.logout_url) {
        logout_url = data.logout_url;
      }
      window.merchantos.utils.openUrl(logout_url);
      return true;
    }

    if (window.merchantos.register.isRegisterRuleAgeVerificationError(data as RegisterRuleData)) {
      return true;
    }

    // capture ajax messages
    this._requestQueueAjaxMessages.push({
      general_msgs: data.general_msgs,
      alert_msg: data.alert_msg,
      success_msg: data.success_msg,
      failed_msg: data.failed_msg,
      metadata: data.metadata,
    });

    if (data.error) {
      window.alertUser(data.error);
      window.clearRequestQueue(data.pannel_id);
      return true;
    }

    if (data.debug) {
      window.addDebug(data.debug);
    }

    if (typeof data.failed_msg === 'string') {
      delay = Math.ceil((data.failed_msg.length + 1) / 8);
      window.flashNotifier('warning', data.failed_msg, delay);
    }

    if (typeof data.success_msg === 'string') {
      delay = Math.ceil((data.success_msg.length + 1) / 8);
      window.flashNotifier('success', data.success_msg, delay);
    }

    if (typeof data.alert_msg === 'string') {
      window.showNotifier('messages', data.alert_msg);
    }

    if (data.need_access_pin) {
      if (data.html) {
        window.no_page_timeouts = false;
        const verify_node = $('#verify_pin');
        if (verify_node.length === 0) {
          window.cb_switch_employees(data);
        }
      } else {
        window.merchantos.utils.openUrl('/register.php');
      }
      return true;
    }

    if (data.add_html) {
      $('body').append(data.add_html);
    }

    return false;
  };

  evalCode = (evalcode: QueueRequest[]) => {
    if (Array.isArray(evalcode)) {
      evalcode.forEach((code) => {
        const request = getRequestFunction(code);
        if (typeof request === 'function') {
          request();
        }
      });
    }
  };

  log_dropped_request = (
    data: AjaxFormsResponse,
    type: 'POST' | 'GET',
    textStatus: JQuery.Ajax.ErrorTextStatus,
    jqXHR: JQuery.jqXHR<any>
  ) => {
    let xml = '<DroppedRequest>';
    xml += `<name>${data.name || data.form_name}</name>`;
    xml += `<httpMethod>${type}</httpMethod>`;
    xml += `<failureReason>${textStatus}</failureReason>`;

    if (typeof jqXHR !== typeof undefined && typeof jqXHR.getAllResponseHeaders === 'function') {
      xml += `<responseHeaders>${jqXHR.getAllResponseHeaders()}</responseHeaders>`;
    }

    xml += '</DroppedRequest>';

    return window.lsCloud.startServerJob({
      type: 'POST',
      url: '/API/DroppedRequest',
      data: xml,
    });
  };

  flushAjaxMessages = () => {
    this._requestQueueAjaxMessages = [];
  };
}

window.merchantos.server_call = new ServerCall();

window.idleTimeout = function idleTimeout() {
  if (window.no_page_timeouts || window.isGriffin()) return;
  const content_map = {} as ContentMap;
  content_map.form_name = 'timeout_check';

  window.merchantos.server_call.bind(content_map, window.resetIdleTimer, true);
};

window.resetIdleTimer = function resetIdleTimer(data = undefined) {
  if (window.idle_timer) {
    window.clearTimeout(window.idle_timer);
  }
  let timeout = window.timeout_delay * 60 * 1000 + 5000;
  if (data) {
    const { last_active_seconds_ago } = data;
    if (last_active_seconds_ago && window.timeout_delay * 60 > last_active_seconds_ago) {
      timeout = (window.timeout_delay * 60 - last_active_seconds_ago) * 1000 + 5000;
    }
  }

  if (window.timeout_delay) {
    window.idle_timer = window.setTimeout(window.idleTimeout, timeout);
  }
};

// @Note Legacy way of finding out the request type to send,
// A way to improve this would be so the BE passes the type in the payload
window.getRequestType = function getRequestType(data) {
  const hasSaveFunction = (data as ContentMap).fnc === 'save';
  const dataLength = JSON.stringify(data).length;

  if (data.method === 'POST' || hasSaveFunction || dataLength >= GET_PARAMS_MAX_LENGTH) {
    return 'POST';
  }

  return 'GET';
};

// If the panel_id is defined, the screens are changing
let inFlightPanelXhrRequest: TSFixMe = null;

window.doServerCall = function doServerCall(data, callback_fnc, options = {}) {
  const phoenixRoute = window.Ashes.createRoute(data);
  let isPanelXhrRequest = false;
  let returnedPromise: JQuery.Promise<any, any, any> | null = null;
  const requestType = window.getRequestType(data);

  if (options.flushAjaxMessages) {
    window.merchantos.server_call.flushAjaxMessages();
  }

  if (phoenixRoute && phoenixRoute.route) {
    window.merchantos.utils.openUrl(phoenixRoute.route);
    returnedPromise = $.Deferred().resolve().promise();
  } else {
    // All of gui_def navigation goes to server_call. When a section, a listing or a view is required
    // or saved, a doServerCall is sent. A frequent error that happens during user's navigation
    // is that they fast click a couple links and get the wrong tab / view is then showing.
    // This is due to the last received GET request replacing the content, but it doesn't mean
    // that the last clicked link is the last received request (in case of a request taking longer
    // than the other). To solve this issue any previous request that was made is aborted to only
    // consider the last click. ONLY for GET requests, POSTS or any other type should not get aborted.
    // More info: https://github.com/merchantos/webPOS/pull/10355/#discussion_r187359188
    if (requestType === 'GET' && data.form_name && /\.(?:open|gettab)$/.test(data.form_name)) {
      if (inFlightPanelXhrRequest) {
        inFlightPanelXhrRequest.abort();
      }
      isPanelXhrRequest = true;
    }
    returnedPromise = window.merchantos.server_call.bind(data, callback_fnc);
    if (isPanelXhrRequest) {
      inFlightPanelXhrRequest = returnedPromise;
    }
  }
  return returnedPromise;
};
